import React, { useState, useEffect, useRef } from "react";
import "./tableStyle.css";
import { reportsApi, exportFreightNotApplicableListExcel } from "../../Services/Services.jsx"
import { Col, Input, Button, Select, message } from "antd";
import { useReactToPrint } from "react-to-print";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "../util.js"; 

const { Option } = Select;

export default function FreightNotApplicableList({ onChange }) {
  const [deliveryOrder, setDeliveryOrder] = useState([]);
  const [pageOffset, setPageOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(50);
  const [exportLoading, setExportLoading] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    do_no: "",
    client_name: "",
  });

  const componentRef = useRef();

  // Debounced fetch function to avoid too many API calls
  const debouncedFetch = useRef(
    debounce((appliedFilters) => {
      setPageOffset(0);
      setDeliveryOrder([]);
      setHasMore(true);
      fetchData(0, appliedFilters);
    }, 800)
  ).current;

  // Initial load when year changes
  useEffect(() => {
    setPageOffset(0);
    setDeliveryOrder([]);
    setHasMore(true);
    fetchData(0, filters);
  }, [filters.year]);

  // Fetch data from backend with all filters
  const fetchData = async (offset = 0, appliedFilters = filters) => {
    try {
      const response = await reportsApi({
        report_name: "is_payment_not_applicable",
        com_name: localStorage.getItem("com_name"),
        limit: limit,
        offset: offset,
        year: appliedFilters.year,
        do_no: appliedFilters.do_no,
        client_name: appliedFilters.client_name,
      });

      if (response && response.results) {
        let filteredData = response.results;

        // Apply client-side filter only for user type (if backend doesn't handle it)
        if (localStorage.getItem("user_type") === "Lifter") {
          filteredData = filteredData.filter(
            (o) =>
              o.lifter_id == localStorage.getItem("user_id") && o.is_applicable
          );
        } else {
          filteredData = filteredData.filter((o) => o.is_applicable);
        }

        if (offset === 0) {
          setDeliveryOrder(filteredData);
        } else {
          setDeliveryOrder((prev) => [...prev, ...filteredData]);
        }

        setHasMore(response.next !== null);
        setPageOffset(offset + limit);
      } else {
        if (offset === 0) {
          setDeliveryOrder([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to fetch data");
      setHasMore(false);
    }
  };

  // Fetch more data for infinite scroll
  const fetchMoreData = () => {
    if (hasMore) {
      fetchData(pageOffset, filters);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...filters, [key]: value };
    setFilters(updatedFilters);

    // Use debounce for text inputs (do_no, client_name)
    if (key === "do_no" || key === "client_name") {
      debouncedFetch(updatedFilters);
    }
    // Year change is handled by useEffect above (no debounce needed)
  };

  // Print handler
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  // Export to Excel handler
  const handleExportToExcel = async () => {
    try {
      setExportLoading(true);

      message.loading({
        content: "Generating Excel file...",
        key: "export",
        duration: 0,
      });

      const response = await exportFreightNotApplicableListExcel({
        com_name: localStorage.getItem("com_name"),
        year: filters.year,
        do_no: filters.do_no,
        client_name: filters.client_name,
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to export Excel file");
      }

      const filename = `Freight_Not_Applicable_List_${filters.year}.xlsx`;

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      message.success({
        content: "Excel file downloaded successfully!",
        key: "export",
        duration: 2,
      });
    } catch (error) {
      console.error("Export error:", error);
      message.error({
        content: `Export failed: ${error.message || "Unknown error"}`,
        key: "export",
        duration: 3,
      });
    } finally {
      setExportLoading(false);
    }
  };

  // Generate years array
  const getYearsArray = (startYear, endYear) => {
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const currentYear = new Date().getFullYear();
  const years = getYearsArray(2023, currentYear);

  return (
    <div>
      <div
        className="grid grid-cols-1 sm:grid sm:grid-cols-4 sm:gap-2 m-auto px-5 sm:w-[70%] md:w-[100%]"
        style={{ marginBottom: "10px" }}
      >
        <Col>
          <label>
            <b>Select Year</b>
          </label>
          <br />
          <Select
            value={filters.year}
            onChange={(value) => handleFilterChange("year", value)}
            showSearch
            style={{ width: "95%", marginLeft: "2%", marginRight: "2%" }}
            placeholder="Select Year"
          >
            {years.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
        </Col>

        <Col>
          <label>
            <b>Search By Do No.</b>
          </label>
          <br />
          <Input
            placeholder="Search by DO No."
            value={filters.do_no}
            onChange={(e) => handleFilterChange("do_no", e.target.value)}
            allowClear
          />
        </Col>

        <Col>
          <label>
            <b>Search By Party Name</b>
          </label>
          <br />
          <Input
            placeholder="Search by Party Name."
            value={filters.client_name}
            onChange={(e) => handleFilterChange("client_name", e.target.value)}
            allowClear
          />
        </Col>

        <Col>
          <Button
            style={{
              top: "10px",
              margin: "10px",
              backgroundColor: "black",
              color: "white",
              fontWeight: "600",
            }}
            onClick={handlePrint}
          >
            <FaFilePdf />
          </Button>
          <Button
            style={{
              top: "10px",
              margin: "10px",
              backgroundColor: "green",
              color: "white",
              fontWeight: "600",
            }}
            onClick={handleExportToExcel}
            loading={exportLoading}
            disabled={exportLoading}
          >
            <FaFileExcel />
          </Button>
        </Col>
      </div>

      <div
        className="table-wrapper-3"
        ref={componentRef}
        id="scrollableDiv"
        style={{
          maxHeight: deliveryOrder.length > 0 ? "500px" : "100px",
          overflowY: deliveryOrder.length > 0 ? "auto" : "visible",
        }}
      >
        <InfiniteScroll
          dataLength={deliveryOrder.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={
            deliveryOrder.length > 0 && hasMore ? (
              <div style={{ textAlign: "center", padding: "10px" }}>
                Loading...
              </div>
            ) : null
          }
          scrollableTarget="scrollableDiv"
        >
          <table id="my-table" className="w-full">
            <thead className="bg-[#151B54] w-full text-white">
              <tr>
                <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                  Sr. No
                </th>
                <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                  Party Name
                </th>
                <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  Do No.
                </th>
                <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  Dis Qty
                </th>
                <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                  Truck Owner Name
                </th>
                <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                  Pan No
                </th>
                <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                  Vehicle No
                </th>
                <th className="lg:text-[12px] text-[0.41rem] p-0 sm:px-2 px-1 lg:p-1 sm:text-base">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              {deliveryOrder.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      fontSize: "14px",
                    }}
                  >
                    No data available
                  </td>
                </tr>
              ) : (
                deliveryOrder.map((e, index) => (
                  <tr key={e.id || index}>
                    <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                      {index + 1}
                    </td>
                    <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                      {e.client_name}
                    </td>
                    <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                      {e.do_no}
                    </td>
                    <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                      {e.dispatch_quantity.toFixed(3)}
                    </td>
                    <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                      {e.truck_owner_name}
                    </td>
                    <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                      {e.pan_no}
                    </td>
                    <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                      {e.vehicle_no}
                    </td>
                    <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                      {e.description}
                    </td>
                  </tr>
                ))
              )}
              {deliveryOrder.length > 0 && (
                <tr style={{ backgroundColor: "#e9e9e9" }}>
                  <td
                    className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"
                    colSpan={3}
                  >
                    <b>Total</b>
                  </td>
                  <td className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base">
                    <b>
                      {deliveryOrder
                        .reduce(
                          (accumulator, currentValue) =>
                            accumulator + +currentValue.dispatch_quantity,
                          0
                        )
                        .toFixed(3)}
                    </b>
                  </td>
                  <td
                    className="lg:text-[12px] text-[0.41rem] p-0 sm:px-3 px-1 lg:p-1 sm:text-base"
                    colSpan={4}
                  ></td>
                </tr>
              )}
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  );
}